// React Imports
import { ReactElement, useEffect, useRef, useState } from "react";

// Third-Party Library Imports
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

// Local Component Imports
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import ApproveModal from "./components/approve-modal";
import DecisionBar from "./components/decision-bar";
import DetailsPanel from "./components/details-panel";
import PreviewPanel from "./components/preview-panel";
import RejectModal from "./components/reject-modal";

// Service Imports
import { CREATE_BREADCRUMB } from "../../store/actions";
import AgreementReviewService from "./agreement-review.service";
import { AgreementReviewStatus } from "../portfolio-agreement/agreement-status";
import {
  applicableDate,
  approvalErrors,
  dateParams,
  feesParams,
} from "../portfolio-agreement/agreement-dates";
import { REVIEW_PERMISSION } from "../portfolio-agreements-bulk/types";

import "./agreement-review.scss";

type Busy = "approve" | "reject" | "preview" | null;

/**
 * WM review of a portfolio agreement amendment or appendix.
 *
 * Only records awaiting a decision can be acted on. The API enforces that with
 * an atomic status claim that answers 409 for a repeated or concurrent
 * decision, which this page treats as "someone got there first" and reloads,
 * rather than reporting a generic failure.
 */
const AgreementReview: React.FC = (): ReactElement => {
  const { agreementId = "" } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user);
  const canReview = user?.permissions?.includes(REVIEW_PERMISSION);
  const service = new AgreementReviewService(dispatch);

  const [record, setRecord] = useState<any>(null);
  /** The one editable value; persisted only on approval. */
  const [dateValue, setDateValue] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Mirrors previewUrl so unmount cleanup sees the latest value rather than
  // the one captured when the effect was created.
  const previewUrlRef = useRef<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  useEffect(() => {
    dispatch({
      type: CREATE_BREADCRUMB,
      payload: {
        breadcrumb: [
          { text: "Portfolios", type: "link", link: "/portfolio" },
          { text: "Portfolio agreements", type: "link", link: "/portfolio?tab=4" },
        ],
      },
    });
  }, []);

  const load = async () => {
    const data = await service.getReview(agreementId);
    if (data) {
      setRecord(data);
      setDateValue(applicableDate(data));
    }
    return data;
  };

  useEffect(() => {
    if (!agreementId || !canReview) return;
    // The document is generated up front so the WM sees what they are deciding
    // on without an extra click, and from the record's own date rather than a
    // default. The endpoint neither stores nor distributes it.
    void (async () => {
      const data = await load();
      await openPreview(applicableDate(data), data?.type);
    })();
  }, [agreementId, canReview]);

  // Release the last object URL when leaving the page. Regenerations revoke
  // the previous one as they replace it, so this only covers unmount.
  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    []
  );

  const openPreview = async (iso?: string | null, type?: string | null) => {
    setBusy("preview");
    const url = await service.getPreviewUrl(
      agreementId,
      dateParams(type ?? record?.type, iso === undefined ? dateValue : iso)
    );
    setBusy(null);
    if (url) {
      // Replace rather than accumulate object URLs across regenerations.
      setPreviewUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        previewUrlRef.current = url;
        return url;
      });
    }
  };

  /** Editing the date regenerates the document it will be approved with. */
  const changeDate = (iso: string | null) => {
    setDateValue(iso);
    void openPreview(iso);
  };

  const decide = async (
    approved: boolean,
    extra: Record<string, unknown> = {}
  ) => {
    setBusy(approved ? "approve" : "reject");
    const outcome = await service.decide(agreementId, { approved, ...extra });
    setBusy(null);
    setApproveOpen(false);
    setRejectOpen(false);

    if (outcome === "success") {
      navigate("/portfolio?tab=4");
      return;
    }
    // A 409 means the record moved on; show its current state rather than a
    // stale form offering decisions that can no longer be made.
    if (outcome === "conflict") void load();
  };

  // Appendices need new fees as well as their date; amendments need only the
  // date. Blocking here saves a round trip the endpoint would reject anyway.
  const blockingErrors = approvalErrors({
    type: record?.type,
    date: dateValue,
    newFees: record?.newFees ?? null,
  });

  const awaitingDecision =
    (record?.reviewStatus ?? "").toUpperCase() ===
    AgreementReviewStatus.PENDING_REVIEW;

  return (
    <section className="agreement-review-container">
      <BreadcrumbComponent />

      <div className="page-title">
        <div>
          <h3>Agreement review</h3>
          <p>
            {record?.portfolioCode
              ? `Portfolio ${record.portfolioCode} — ${record?.clientNameEn ?? ""}`
              : `Request #${agreementId}`}
          </p>
        </div>
      </div>

      {!canReview ? (
        <div className="panel">
          <div className="body">
            <div className="notice is-error">
              <p className="title">You do not have access to WM review</p>
              <p>
                This page needs the review new portfolio agreement permission.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <DetailsPanel
            record={record}
            awaitingDecision={awaitingDecision}
            dateValue={dateValue}
            onDateChange={changeDate}
            regenerating={busy === "preview"}
          />

          <PreviewPanel
            previewUrl={previewUrl}
            generating={busy === "preview"}
            disabled={busy !== null}
            onGenerate={() => void openPreview()}
          />

          <DecisionBar
            enabled={awaitingDecision && busy === null}
            approveErrors={blockingErrors}
            onApprove={() => setApproveOpen(true)}
            onReject={() => setRejectOpen(true)}
          />
        </>
      )}

      <ApproveModal
        isVisible={approveOpen}
        busy={busy === "approve"}
        type={record?.type}
        dateValue={dateValue}
        newFees={record?.newFees ?? null}
        onClose={() => setApproveOpen(false)}
        // The date goes under the key its type uses; fees only where required.
        onConfirm={() =>
          void decide(true, {
            ...dateParams(record?.type, dateValue),
            ...feesParams(record?.type, record?.newFees),
          })
        }
      />
      <RejectModal
        isVisible={rejectOpen}
        busy={busy === "reject"}
        onClose={() => setRejectOpen(false)}
        onConfirm={(feedback) => void decide(false, { feedback })}
      />
    </section>
  );
};

export default AgreementReview;
